import { Add } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import {
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Skeleton,
} from '@mui/material'
import AppBar from '@mui/material/AppBar'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import Slide from '@mui/material/Slide'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import { X } from 'lucide-react'
import { forwardRef, Fragment, useEffect, useState } from 'react'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction='up' ref={ref} {...props} />
})

export default function SearchSongsDialog({
  openSearchSongsDialog,
  handleCloseSearchSongsDialog,
  handleAddVideo,
  query,
  debouncedQuery,
  setDebouncedQuery,
}) {
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedQuery(query)
    }, 500)

    return () => clearTimeout(timeout)
  }, [query])

  const fetchVideos = async () => {
    try {
      const url = `https://www.googleapis.com/youtube/v3/search?part=snippet&q=${debouncedQuery}&type=video&maxResults=100&key=${process.env.REACT_APP_YT_KEY}`

      const res = await fetch(url)
      const data = await res.json()

      if (data.error?.code === 403 && data.error?.message.includes('quota')) {
        toast.error(
          'YouTube API quota exceeded. Please log in with Google to continue.',
          { icon: <X color='white' />, style: { color: 'white' } }
        )
        return
      }

      setResults(
        data.items.map((item) => ({
          videoId: item.id.videoId,
          title: item.snippet.title,
          thumbnail: item.snippet.thumbnails.medium.url,
          author_name: item.snippet.channelTitle,
        }))
      )
    } catch (error) {
      console.error('Error fetching videos:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchYoutubeData = async (token) => {
    try {
      const res = await fetch(
        `https://www.googleapis.com/youtube/v3/search?part=snippet&q=${debouncedQuery}&type=video&maxResults=100`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      const data = await res.json()
      if (data.error?.code === 403 && data.error?.message.includes('quota')) {
        toast.error(
          'YouTube API quota exceeded. Please log in with Email to continue.',
          { icon: <X color='white' />, style: { color: 'white' } }
        )
        return
      }
      setResults(
        data.items.map((item) => ({
          videoId: item.id.videoId,
          title: item.snippet.title,
          thumbnail: item.snippet.thumbnails.medium.url,
          author_name: item.snippet.channelTitle,
        }))
      )
    } catch (error) {
      console.error(error, 'error during searching')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!debouncedQuery) return
    setLoading(true)
    const token = localStorage.getItem('ytToken')
    token ? fetchYoutubeData(token) : fetchVideos()
  }, [debouncedQuery])

  return (
    <Fragment>
      <Dialog
        fullScreen
        open={openSearchSongsDialog}
        onClose={handleCloseSearchSongsDialog}
        slots={{
          transition: Transition,
        }}
        sx={{
          bgcolor: '#010000',
          '& .MuiDialog-paper': { bgcolor: '#010000' },
        }}
      >
        <AppBar sx={{ position: 'sticky', bgcolor: '#1e1e1e' }}>
          <Toolbar>
            <IconButton
              edge='start'
              color='inherit'
              onClick={handleCloseSearchSongsDialog}
              aria-label='close'
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant='h6' component='div'>
              Results for: {debouncedQuery}
            </Typography>
          </Toolbar>
        </AppBar>

        <List sx={{ color: 'white' }}>
          {loading
            ? // Show skeleton cards while loading
              Array.from(new Array(6)).map((_, index) => (
                <Card
                  key={index}
                  sx={{
                    bgcolor: 'inherit',
                    color: 'inherit',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    padding: 1,
                    marginBottom: 1,
                  }}
                >
                  <Skeleton
                    variant='rectangular'
                    width='calc(600px - 240px)'
                    height={200}
                    sx={{
                      borderRadius: 1,
                      flexShrink: 0,
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    }}
                  />
                  <CardContent sx={{ flex: 1, padding: '8px 16px !important' }}>
                    <Skeleton
                      variant='text'
                      width='80%'
                      height={32}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        marginBottom: 1,
                      }}
                    />
                    <Skeleton
                      variant='text'
                      width='60%'
                      height={20}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        marginBottom: 2,
                      }}
                    />
                    <Skeleton
                      variant='rectangular'
                      width={120}
                      height={30}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: 1,
                      }}
                    />
                  </CardContent>
                </Card>
              ))
            : // Show actual results when loaded
              results.map((item, index) => (
                <Card
                  key={index}
                  sx={{
                    bgcolor: 'inherit',
                    color: 'inherit',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    padding: 1,
                    marginBottom: 1,
                    '&:hover': {
                      bgcolor: '#1e1e1e',
                    },
                  }}
                >
                  <CardActionArea
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      padding: 0,
                    }}
                  >
                    <CardMedia
                      component='img'
                      sx={{
                        width: 'calc(600px - 240px)',
                        height: 200,
                        borderRadius: 1,
                        flexShrink: 0,
                        objectFit: 'cover',
                      }}
                      image={item?.thumbnail}
                      alt='Song thumbnail'
                    />

                    <CardContent
                      sx={{
                        flex: 1,
                        padding: '8px 16px !important',
                        '&:last-child': { paddingBottom: '8px !important' },
                      }}
                    >
                      <Typography
                        variant='h5'
                        component='h5'
                        sx={{
                          fontWeight: 500,
                          lineHeight: 1.3,
                          marginBottom: 0.5,
                          color: 'inherit',
                        }}
                      >
                        {item?.title}
                      </Typography>

                      <Typography
                        variant='body2'
                        color='inherit'
                        sx={{
                          lineHeight: 1.2,
                          marginBottom: 0.5,
                        }}
                      >
                        {item?.author_name}
                      </Typography>

                      <Button
                        startIcon={<Add />}
                        variant='outlined'
                        sx={{
                          border: '1px solid #fff',
                          color: '#fff',
                          '&:hover': {
                            fontWeight: 'bold',
                            border: '1px solid #fff',
                            bgcolor: 'rgba(255, 255, 255, 0.3)',
                          },
                        }}
                        size={'small'}
                        onClick={() => {
                          handleAddVideo(item?.videoId)
                          toast.success('add in queue')
                        }}
                      >
                        Add To Queue
                      </Button>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
        </List>
      </Dialog>
      <ToastContainer position={'top-right'} />
    </Fragment>
  )
}
